import {ChangeDetectionStrategy, Component, inject, input, OnInit, signal, WritableSignal} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {EntityAssetViewModel} from '../view-models/entity-asset.view-model';
import {EntityAssetService} from '../services/entity-asset.service';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {EntityAssetTransactionViewModel} from '../view-models/entity-asset-transaction.view-model';
import {EntityAssetTransactionConverterService} from '../services/entity-asset-transaction-converter.service';
import {Keys} from '@shared/keys';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {BaseEntityDetailsModal} from '../../shared/base-entity-details-modal';
import {EntityTransactionModalComponent} from '../../shared/entity-transaction-modal/entity-transaction-modal.component';

@Component({
    selector: 'app-entity-asset-details-modal',
    templateUrl: './entity-asset-details-modal.component.html',
    styleUrls: ['./entity-asset-details-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ClientsideDataService],
    imports: [EntityTransactionModalComponent]
})
export class EntityAssetDetailsModalComponent
  extends BaseEntityDetailsModal<EntityAssetViewModel, EntityAssetTransactionViewModel>
  implements OnInit {

  readonly modalDataInput = input.required<EntityAssetViewModel>();

  readonly entityAsset: WritableSignal<EntityAssetViewModel> = signal(undefined as unknown as EntityAssetViewModel);

  private readonly entityAssetService = inject(EntityAssetService);
  private readonly entityAssetTransactionConverterService = inject(EntityAssetTransactionConverterService);

  ngOnInit() {
    this.entityAsset.set(this.modalDataInput());
    this.initializeModal(this.entityAsset(), 'EntityAssetDetailsModal');
  }

  protected populateColumns(): TableTemplateColumnConfig[] {
    return [
      {
        title: 'Date',
        dataProperty: 'date',
        cellType: CellType.Date,
        sortable: true,
        alignment: Alignment.Left,
        width: '27%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Type',
        dataProperty: 'type',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '23%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Description',
        dataProperty: 'description',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '30%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount',
        dataProperty: 'amount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '20%',
        dataFocusable: false,
        headingFocusable: true
      }
    ];
  }

  protected getSearchFields(): Keys<EntityAssetTransactionViewModel> {
    return ['type', 'description'];
  }

  protected getSortedTable(): SortedTable {
    return SortedTable.Assets;
  }

  protected getTransactionSortedTable(): SortedTable {
    return SortedTable.AssetTransactions;
  }

  protected override shouldSkipLoad(entity: EntityAssetViewModel): boolean {
    return !entity.portfolioNumber || !entity.id;
  }

  protected loadTransactions(entity: EntityAssetViewModel): Observable<EntityAssetTransactionViewModel[]> {
    return this.entityAssetService.getEntityAssetTransactions(entity.entityCode, entity.id).pipe(
      map(transactionList => this.entityAssetTransactionConverterService.convert(transactionList))
    );
  }
}
